import axios from 'axios';
import Constants from 'expo-constants';
import { SpallaLiveStreamResponse, SpallaSubtitles, SpallaVideoResponse } from 'types';

const getCountryInfo = async () => {
  try {
    const response = await axios.get(
      'https://bandplay-client-eaaqbfc8dfgjfect.eastus2-01.azurewebsites.net/api/info'
    );
    return response.data.country;
  } catch (error) {
    console.log('Erro:', error);
  }
};

const spallaClient = axios.create({
  baseURL: 'https://api.spalla.io/v1',
  headers: {
    Authorization: `Bearer ${Constants.expoConfig?.extra?.spallaToken}`,
  },
});

const checkGeoAccess = async (
  geoAccessControl: string,
  geoWhitelist: string[],
  geoBlacklist: string[]
): Promise<boolean> => {
  const country = await getCountryInfo();

  if (!country) {
    return true;
  }

  if (!geoAccessControl) {
    return true;
  }

  if (!geoAccessControl) {
    return true;
  }

  if (geoAccessControl === 'whitelist') {
    if (geoWhitelist.length === 0) {
      return true;
    }
    return country ? geoWhitelist.includes(country + '*') : true;
  }

  if (geoAccessControl === 'blacklist') {
    if (geoBlacklist.length === 0) {
      return true;
    }
    return country ? !geoBlacklist.includes(country + '*') : true;
  }

  return true;
};

const getSpallaVideoInfo = async (videoId?: string): Promise<SpallaVideoResponse | null> => {
  if (!videoId) return null;

  try {
    const { data } = await spallaClient.get<SpallaVideoResponse>(`/videos/${videoId}`);
    return data;
  } catch (error) {
    return null;
  }
};

const getSpallaVideoTextTracksUrl = async (videoId: string): Promise<Record<string, string>> => {
  const videoData = await getSpallaVideoInfo(videoId);

  if (!videoData) {
    throw new Error('VIDEO_NOT_FOUND');
  }

  const { geoAccessControl, geoWhitelist, geoBlacklist, assets } = videoData;

  const isGeoAllowed = await checkGeoAccess(
    geoAccessControl,
    geoWhitelist ?? [],
    geoBlacklist ?? []
  );

  if (!isGeoAllowed) {
    throw new Error('GEOLOCATION_ERROR');
  }

  return (assets.availableSrt as Record<string, string>) ?? {};
};

const getSpallaVideoStreamUrl = async (videoId: string): Promise<SpallaVideoResponse> => {
  const videoData = await getSpallaVideoInfo(videoId);

  if (!videoData) {
    throw new Error('VIDEO_NOT_FOUND');
  }

  const isGeoAllowed = await checkGeoAccess(
    videoData.geoAccessControl,
    videoData.geoWhitelist || [],
    videoData.geoBlacklist || []
  );

  if (isGeoAllowed) {
    return videoData as SpallaVideoResponse;
  } else {
    throw new Error('GEOLOCATION_ERROR');
  }
};

const getSpallaLiveStreamUrl = async (liveId: string): Promise<SpallaLiveStreamResponse> => {
  if (!liveId) {
    return {} as SpallaLiveStreamResponse;
  }

  const country = await getCountryInfo();
  const response = await spallaClient.get<SpallaLiveStreamResponse>(`/live-streams/${liveId}`);

  if (country) {
    if (country !== 'BR') {
      throw new Error('Vídeo indisponível no seu país');
    }
    if (response.status !== 200) {
      throw new Error();
    }
    return response.data as SpallaLiveStreamResponse;
  } else {
    return response.data as SpallaLiveStreamResponse;
  }
};

const getSpallaMediaUrl = async (mediaId: string, isLive = false): Promise<string> => {
  if (isLive) {
    const { assets } = await getSpallaLiveStreamUrl(mediaId);
    return assets.hls;
  } else {
    const { assets } = await getSpallaVideoStreamUrl(mediaId);
    return assets.hls;
  }
};

const getSpallaSubtitlesPtBr = async (
  videoId: string,
  language: string
): Promise<SpallaSubtitles> => {
  if (!videoId) return [];

  try {
    const { status, data } = await spallaClient.get<SpallaSubtitles>(
      `/videos/${videoId}/subtitles?language=${language}`
    );
    if (status !== 200) return [];
    return [...data].sort((a, b) => a.inicio_numerico - b.inicio_numerico);
  } catch (error) {
    return [];
  }
};

const getSpallaCheckSubtitles = async (videoId: string | undefined): Promise<boolean> => {
  try {
    const videoData = await getSpallaVideoInfo(videoId);

    if (!videoData) {
      throw new Error('VIDEO_NOT_FOUND');
    }

    return videoData?.subtitles;
  } catch (error) {
    return false;
  }
};

export {
  getSpallaMediaUrl,
  getSpallaSubtitlesPtBr,
  getSpallaVideoTextTracksUrl,
  getSpallaCheckSubtitles,
};
